/**
 * Vocabulary of the continuous timeline (ADR-0006): segment states, detection types, severity,
 * review states and the score tree labels. English source strings; translate with t().
 */
import type { LucideIcon } from 'lucide-react';
import {
  ArrowDownRight,
  ArrowUpRight,
  Ban,
  CameraOff,
  CarFront,
  Cigarette,
  Coffee,
  EyeOff,
  Fuel,
  Hourglass,
  Moon,
  RotateCcw,
  ShieldAlert,
  Siren,
  Smartphone,
  TriangleAlert,
  Users,
  Waypoints,
  Zap,
} from 'lucide-react';
import { formatDuration, formatNumber, formatSpeed } from '@/lib/format';
import { msg, t, tp } from '@/lib/i18n';
import type { Tone } from '@/ui';
import type { Detection } from './api';

export type SegmentState = 'driving' | 'idling' | 'stopped' | 'pto' | 'parked' | 'offline';
export type TimelineTone = 'primary' | 'good' | 'medium' | 'bad' | 'neutral' | 'accent';

/** Each state has its own look on the timeline; offline is hatched (no data, not "parked"). */
export const STATES: Record<SegmentState, { label: string; description: string; tone: TimelineTone; pattern?: boolean }> = {
  driving: { label: msg('Driving'), description: msg('Ignition on and moving'), tone: 'primary' },
  idling: { label: msg('Idling'), description: msg('Engine on, standing still, no PTO'), tone: 'medium' },
  stopped: { label: msg('Stopped'), description: msg('Short stop with the engine on'), tone: 'accent' },
  pto: { label: msg('PTO'), description: msg('Power take-off working while standing'), tone: 'good' },
  parked: { label: msg('Parked'), description: msg('Engine off'), tone: 'neutral' },
  offline: { label: msg('No data'), description: msg('The device sent nothing'), tone: 'neutral', pattern: true },
};

export const STATE_ORDER: SegmentState[] = ['driving', 'idling', 'stopped', 'pto', 'parked', 'offline'];

export function stateOf(key: string): (typeof STATES)[SegmentState] {
  return STATES[key as SegmentState] ?? { label: key, description: '', tone: 'neutral' };
}

export type DetectionFamily = 'vehicle_control' | 'speed' | 'road_sharing' | 'attention' | 'eco' | 'vehicle';

export const FAMILIES: Record<DetectionFamily, string> = {
  vehicle_control: msg('Vehicle control'),
  speed: msg('Speed management'),
  road_sharing: msg('Road sharing'),
  attention: msg('Attention'),
  eco: msg('Eco-driving'),
  vehicle: msg('Vehicle'),
};

interface DetectionDef {
  label: string;
  family: DetectionFamily;
  icon: LucideIcon;
}

export const DETECTIONS: Record<string, DetectionDef> = {
  harsh_accel: { label: msg('Harsh acceleration'), family: 'vehicle_control', icon: ArrowUpRight },
  harsh_brake: { label: msg('Harsh braking'), family: 'vehicle_control', icon: ArrowDownRight },
  harsh_corner: { label: msg('Harsh cornering'), family: 'vehicle_control', icon: RotateCcw },
  speeding: { label: msg('Speeding'), family: 'speed', icon: Zap },
  tailgating: { label: msg('Tailgating'), family: 'road_sharing', icon: CarFront },
  fcw: { label: msg('Forward collision warning'), family: 'road_sharing', icon: ShieldAlert },
  pcw: { label: msg('Pedestrian collision warning'), family: 'road_sharing', icon: Users },
  ldw: { label: msg('Lane departure warning'), family: 'road_sharing', icon: Waypoints },
  distraction: { label: msg('Distraction'), family: 'attention', icon: EyeOff },
  phone: { label: msg('Phone use'), family: 'attention', icon: Smartphone },
  yawning: { label: msg('Yawning'), family: 'attention', icon: Moon },
  eyes_closed: { label: msg('Eyes closed'), family: 'attention', icon: EyeOff },
  smoking: { label: msg('Smoking'), family: 'attention', icon: Cigarette },
  fatigue: { label: msg('Fatigue'), family: 'attention', icon: Coffee },
  camera_blocked: { label: msg('Driver camera blocked'), family: 'attention', icon: CameraOff },
  idling: { label: msg('Long idling'), family: 'eco', icon: Hourglass },
  pto: { label: msg('PTO'), family: 'eco', icon: Siren },
  fuel_drop: { label: msg('Fuel drop'), family: 'vehicle', icon: Fuel },
};

export function detectionDef(type: string): DetectionDef {
  return DETECTIONS[type] ?? { label: type.replace(/_/g, ' '), family: 'vehicle', icon: TriangleAlert };
}

/** The label of a detection type, translated (unknown types show their key without underscores). */
export function detectionLabel(type: string): string {
  const d = DETECTIONS[type];
  return d ? t(d.label) : type.replace(/_/g, ' ');
}

export const SEVERITIES: Record<string, { label: string; tone: Tone; timeline: TimelineTone }> = {
  high: { label: msg('High'), tone: 'bad', timeline: 'bad' },
  medium: { label: msg('Medium'), tone: 'caution', timeline: 'medium' },
  low: { label: msg('Low'), tone: 'info', timeline: 'primary' },
};

export function severityOf(key: string): { label: string; tone: Tone; timeline: TimelineTone } {
  return SEVERITIES[key] ?? { label: key, tone: 'neutral', timeline: 'neutral' };
}

export type ReviewState = 'unreviewed' | 'confirmed' | 'dismissed';

export const REVIEWS: Record<ReviewState, { label: string; tone: Tone; icon?: LucideIcon }> = {
  unreviewed: { label: msg('Not reviewed'), tone: 'neutral' },
  confirmed: { label: msg('Confirmed'), tone: 'good' },
  dismissed: { label: msg('Dismissed'), tone: 'neutral', icon: Ban },
};

export function reviewOf(d: Pick<Detection, 'review'>): ReviewState {
  return d.review === 'confirmed' || d.review === 'dismissed' ? d.review : 'unreviewed';
}

/** What a detection's `value` means depends on its type (peak g, headway, litres…). */
export function detectionValue(d: Pick<Detection, 'type' | 'value' | 'data' | 'duration_s'>): string | null {
  const data = d.data as Record<string, number | undefined>;
  if (d.type.startsWith('harsh_')) return d.value != null ? `${formatNumber(d.value, 2)} g` : null;
  if (d.type === 'speeding') {
    const max = data.max_speed;
    const limit = data.limit;
    if (max != null && limit != null) return t('{speed} in a {limit} zone', { speed: formatSpeed(max), limit: formatSpeed(limit) });
    return null;
  }
  if (d.type === 'tailgating') return d.value != null ? t('{seconds} s headway', { seconds: formatNumber(d.value, 1) }) : null;
  if (d.type === 'fuel_drop') return data.litres != null ? `${formatNumber(data.litres, 1)} L` : null;
  if (d.type === 'idling' || d.type === 'camera_blocked' || d.type === 'pto') return formatDuration(d.duration_s ?? d.value);
  if (DETECTIONS[d.type]?.family === 'attention' && d.value != null) return tp(Math.round(d.value), '{count} alert', '{count} alerts');
  return d.value != null ? formatNumber(d.value, 1) : null;
}

/** The score tree users know (server: telemetry/scoring.py); labels for the web. */
export const SCORE_LABELS: Record<string, string> = {
  safety: msg('Safety'),
  vehicle_control: msg('Vehicle control'),
  acceleration: msg('Acceleration'),
  deceleration: msg('Braking'),
  steering: msg('Cornering'),
  road_sharing: msg('Road sharing'),
  tailgating: msg('Following distance'),
  speed_management: msg('Speed management'),
  speeding: msg('Speeding'),
  attention: msg('Attention'),
  sleepiness: msg('Alertness'),
  eyes_closed: msg('Eyes closed'),
  yawning: msg('Yawning'),
  focus: msg('Focus'),
  phone_use: msg('Phone use'),
  eco: msg('Eco-efficiency'),
  standstill: msg('Standstill'),
  idling: msg('Idling'),
  engine_gear: msg('Engine & gear'),
  engine_load: msg('Engine load'),
  speed_adaptation: msg('Speed adaptation'),
  cruise_control: msg('Cruise control'),
  anticipation: msg('Anticipation'),
  coasting: msg('Coasting'),
};

export function scoreLabel(key: string, fallback?: string): string {
  const l = SCORE_LABELS[key];
  return l ? t(l) : (fallback ?? key.replace(/_/g, ' '));
}

/** Exposure measures: what must be observed before a score exists (units for "x of y"). */
export const EXPOSURE_UNITS: Record<string, 'm' | 's'> = {
  harsh_exposure_m: 'm',
  dsm_exposure_m: 'm',
  headway_exposure_s: 's',
  limit_exposure_s: 's',
  idle_exposure_s: 's',
  load_exposure_s: 's',
  cruise_exposure_s: 's',
  coast_exposure_s: 's',
};

export const EXPOSURE_LABELS: Record<string, string> = {
  harsh_exposure_m: msg('distance with an accelerometer'),
  dsm_exposure_m: msg('distance with a working driver camera'),
  headway_exposure_s: msg('driving with headway monitoring'),
  limit_exposure_s: msg('driving with a known speed limit'),
  idle_exposure_s: msg('engine-on time with a reliable engine state'),
  load_exposure_s: msg('driving with an engine-load signal'),
  cruise_exposure_s: msg('driving with a cruise-control signal'),
  coast_exposure_s: msg('driving with coasting signals'),
};

/** A score band for timeline blocks and markers (the three-band scale). */
export function scoreTone(score: number | null | undefined): TimelineTone {
  if (score == null) return 'neutral';
  if (score >= 80) return 'good';
  if (score >= 60) return 'medium';
  return 'bad';
}
